/* eslint-disable max-lines */
import React, {
    createRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type {FormEvent, KeyboardEvent} from 'react';
import keyBy from 'lodash/keyBy';
import debounce from 'lodash/debounce';

import pick from '@tinkoff/utils/object/pick';
import isEqual from '@tinkoff/utils/is/equal';

import {useEventCallback} from 'usehooks-ts';

import {addListener} from '@reduxjs/toolkit';

import type {IEditor} from 'packages/editor';
import {isContentBlockEmpty} from 'packages/editor';

import {Posts} from 'mattermost-redux/constants';
import {sortFileInfos} from 'mattermost-redux/utils/file_utils';

import * as GlobalActions from 'actions/global_actions';
import Constants, {Locations, ModalIdentifiers} from 'utils/constants';
import {
    containsAtChannel,
    editLastPostKeyPressAdvanced,
    groupsMentionedInText,
    isErrorInvalidSlashCommand,
    isNotAvailableServerError,
    isSystemMessage,
    postMessageOnKeyPressAdvanced,
    shouldFocusMainTextbox,
    specialMentionsInText,
} from 'utils/post_utils';
import * as UserAgent from 'utils/user_agent';
import * as Utils from 'utils/utils';
import {getTimestamp} from 'utils/datetime';

import NotifyConfirmModal from 'components/notify_confirm_modal';

import ResetStatusModal from 'components/reset_status_modal';
import type {Emoji} from 'mattermost-redux/types/emojis';
import type {FileInfo} from 'mattermost-redux/types/files';
import type {ServerError} from 'mattermost-redux/types/errors';
import type {CommandArgs} from 'mattermost-redux/types/integrations';
import type {Post, PostMetadata} from 'mattermost-redux/types/posts';
import type {FilePreviewInfo} from 'components/file_preview/file_preview';
import type {FileUpload as FileUploadClass} from 'components/file_upload/file_upload';
import {TimeLogger} from 'utils/logger';

import DeletePostModal from 'components/delete_post_modal';

import type {EditorProps} from '../editor/types';

import {Client4} from 'mattermost-redux/client';
import {sendToStatist} from 'packages/statist';
import {
    getPluginItems,
    getStatusFromSlashCommand,
    isStatusSlashCommand,
    getSpecialMentions,
} from '../editor/helpers';
import {useAppDispatch} from 'stores/redux_store';
import {removedDraft} from 'features/drafts/logic/slice';
import {getDraftIdFromKey} from 'features/drafts/utils';
import {mark, Marks} from '@time-webkit/performance';
import {asyncResult} from 'utils/async_result';
import {validateHashtagsLength} from 'utils/text_formatting';

import {ChannelType} from '@time-webkit/all/types/general';

import {toMarkdown} from 'packages/editor/src/to_markdown';

import {useGetCreateMessage} from './useGetCreateMessage';

// Temporary fix for IE-11, see MM-13423
function trimRight(str: string) {
    if (String.prototype.trimRight as any) {
        return str.trimRight();
    }

    return str.replace(/\s*$/, '');
}

const KeyCodes = Constants.KeyCodes;
const SPECIAL_MENTIONS = getSpecialMentions();

export const useEditor = (props: EditorProps) => {
    const [quote, setQuote] = useState<Post | undefined>(props.draft.quote);
    const [caretPosition, setCaretPosition] = useState<number>(props.draft.message.length ?? 0);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [editing, setEditing] = useState<boolean>(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
    const [uploadsProgressPercent, setUploadsProgressPercent] = useState<{[clientID: string]: FilePreviewInfo}>({});
    const [serverError, setServerError] = useState<(ServerError & {submittedMessage?: string}) | null>(null);
    const [clientUploadError, setClientUploadError] = useState<string | null>(null);
    const [fileInfos, setFileInfos] = useState<FileInfo[]>(props.draft.fileInfos);
    const [uploadsInProgress, setUploadsInProgress] = useState<string[]>([]);
    const [message, setMessage] = useState<string>(props.draft.message);

    const editorRef = useRef<IEditor | null>(null);

    const isTextboxFocused = editorRef.current?.isFocused;
    const [lastChannelSwitchAt] = useState(() => Date.now());

    const topDiv = createRef<HTMLFormElement>();
    const fileUploadRef = createRef<FileUploadClass>();
    const createPostControlsRef = React.useRef<HTMLSpanElement>(null);
    const emojiPickerRef = React.useRef<HTMLButtonElement>(null);

    const [lastOrientation, setLastOrientation] = useState<string>();
    const [isMounted, setIsMounted] = useState(false);
    const hashtagError = useMemo(() => {
        if (!validateHashtagsLength(message)) {
            return props.intl.formatMessage({
                id: 'editor.errors.hashtag_limit',
                defaultMessage: 'You can add up to 1 000 symbols to hashtags',
            });
        }

        return null;
    }, [message, props.intl]);
    const postError = useMemo(() => {
        if (!clientUploadError && !hashtagError) {
            return '';
        }

        if (!clientUploadError) {
            return hashtagError;
        }

        if (!hashtagError) {
            return clientUploadError;
        }

        let separator = ' ';
        if (clientUploadError[clientUploadError.length - 1] !== '.') {
            separator = '. ';
        }

        return clientUploadError + separator + hashtagError;
    }, [clientUploadError, hashtagError]);

    const dispatch = useAppDispatch();

    const hasMessageOrFiles = Boolean(message) || fileInfos?.length > 0;

    useEffect(() => {
        const unsubscribe = dispatch(
            addListener({
                actionCreator: removedDraft,
                effect: ({payload}) => {
                    const draftId = getDraftIdFromKey(payload);

                    // Поскольку теперь всегда создается пустой черновик, необходимо также
                    // проверять наличие текста или аттачментов
                    if (draftId && props.draft?.draftId === draftId && hasMessageOrFiles) {
                        editorRef.current?.commands.clearContent(false);
                        setFileInfos([]);
                        setUploadsInProgress([]);
                        setQuote(undefined);
                        setMessage('');
                    }
                },
            }),
        );

        return () => {
            dispatch(unsubscribe);
        };
    }, [dispatch, props.draft?.draftId, hasMessageOrFiles]);

    useEffect(() => {
        onOrientationChange();
        focusTextbox();

        // @TODO: перенести обработку paste в редактор
        document.addEventListener('keydown', documentKeyHandler);
        window.addEventListener('beforeunload', unloadHandler);
        window.addEventListener('message', handleMessageEvent);
        setOrientationListeners();

        setIsMounted(true);

        return () => {
            saveDraft();

            // делал ребэйз нужно будет разобраться
            // eslint-disable-next-line react-hooks/exhaustive-deps
            uploadsInProgress?.forEach((id) => fileUploadRef.current?.cancelUpload(id));

            document.removeEventListener('keydown', documentKeyHandler);
            window.removeEventListener('beforeunload', unloadHandler);
            window.removeEventListener('message', handleMessageEvent);
            removeOrientationListeners();

            setIsMounted(false);
        };

        // делал ребэйз нужно будет разобраться
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        /**
            * при смене обсуждения позволяет отменить редактирование поста
            * @TODO: вынести логику с редактированием в отдельный хук
        */

        return () => {
            if (props.isEditing) {
                props.actions.unsetEditingPost();
            }
        };
    }, [props.isEditing, props.actions]);

    const saveDraft = useEventCallback((
        _message: string = message,
        _fileInfos: FileInfo[] = fileInfos,
        _quote: Post | null | undefined = quote,
    ) => {
        if (!isMounted || props.isEditing) {
            return;
        }

        if (submitting) {
            return;
        }

        const currentDraft = {
            message: _message,
            fileInfos: _fileInfos,
            quote: _quote || undefined,
        };

        if (isEqual(pick(['fileInfos', 'message', 'quote'], props.draft), currentDraft)) {
            return;
        }

        if (props.currentChannelId) {
            TimeLogger.debug('SetDraft called with', {
                draft: currentDraft,
                id: props.isThread ? props.rootId! : props.currentChannelId,
                isThread: props.isThread,
            });
            props.actions.setDraft({
                draft: currentDraft,
                id: props.isThread ? props.rootId! : props.currentChannelId,
                isThread: props.isThread,
            });
        }
    });

    const handleSaveDraftOnMessageChange = useMemo(() => debounce(saveDraft, 1000), [saveDraft]);

    const getMarkdown = () => {
        if (!editorRef.current) {
            return '';
        }
        return toMarkdown(editorRef.current.state, editorRef.current.schema);
    };

    const setMarkdownToEditor = (message: string, emitUpdate: boolean) => {
        return editorRef.current?.chain().setMarkdown(message, emitUpdate).focus().run();
    };

    const focusTextboxIfNecessary = (e: globalThis.KeyboardEvent) => {
        // Focus should go to the RHS when it is expanded
        if (!props.isThread && props.rhsExpanded) {
            return;
        }

        // Do not focus if the rhs is not expanded and this is the RHS
        if (props.isThread && !props.rhsExpanded) {
            return;
        }

        // Do not focus the main textbox when the RHS is open as a hacky fix to avoid cursor jumping textbox sometimes
        if (props.isThread && props.rhsOpened && document.activeElement?.tagName === 'BODY') {
            return;
        }

        // Bit of a hack to not steal focus from the channel switch modal if it's open
        // This is a special case as the channel switch modal does not enforce focus like
        // most modals do
        if (document.getElementsByClassName('channel-switch-modal').length) {
            return;
        }

        if (shouldFocusMainTextbox(e, document.activeElement)) {
            focusTextbox();
        }
    };

    const documentKeyHandler = (e: globalThis.KeyboardEvent) => {
        focusTextboxIfNecessary(e);
    };

    const handleMessageEvent = useEventCallback((event: MessageEvent) => {
        if (!event.data || event.origin !== window.location.origin) {
            return;
        }

        const threadLocations = [Locations.RHS_COMMENT, Locations.RHS_ROOT];

        const postInCurrentThread = event.data.post?.root_id === props.rootId;
        const postIsRootMessage = event.data.post?.id === props.rootId;

        const isReplyToPostEvent =
            event.data.type === 'reply_to_message' &&
            Boolean(event.data.isRHS) === Boolean(props.isThread) &&
            (!props.rootId || postInCurrentThread || postIsRootMessage) &&
            (props.isThread ? threadLocations.includes(event.data.location) : event.data.location === Locations.CENTER);

        if (isReplyToPostEvent) {
            const post = event.data.post as Post;

            setQuote(post);
            saveDraft(message, fileInfos, post);
            props.onReplyHandle?.();
            editorRef.current?.commands.focus();
        }

        if (event.data.type === 'post_deleted') {
            const postId: string = event.data.postId;

            if (quote?.id === postId) {
                handlePostQuoteClose();
            }
        }
    });

    const unloadHandler = useEventCallback(() => {
        saveDraft(message, fileInfos, quote);
    });

    const onOrientationChange = () => {
        if (!UserAgent.isIosWeb()) {
            return;
        }

        const LANDSCAPE_ANGLE = 90;
        let orientation = 'portrait';
        if (window.orientation) {
            orientation = Math.abs(window.orientation as number) === LANDSCAPE_ANGLE ? 'landscape' : 'portrait';
        }

        if (window.screen.orientation) {
            orientation = window.screen.orientation.type.split('-')[0]!;
        }

        if (lastOrientation && orientation !== lastOrientation && isTextboxFocused) {
            editorRef.current?.commands.blur();
        }

        setLastOrientation(orientation);
    };

    const setOrientationListeners = () => {
        if (window.screen.orientation && 'onchange' in window.screen.orientation) {
            window.screen.orientation.addEventListener('change', onOrientationChange);
        } else if ('onorientationchange' in window) {
            window.addEventListener('orientationchange', onOrientationChange);
        }
    };

    const removeOrientationListeners = () => {
        if (window.screen.orientation && 'onchange' in window.screen.orientation) {
            window.screen.orientation.removeEventListener('change', onOrientationChange);
        } else if ('onorientationchange' in window) {
            window.removeEventListener('orientationchange', onOrientationChange);
        }
    };

    const clearChannelDraft = () => {
        const {currentChannelId} = props;

        props.actions.setDraft({
            draft: null,
            id: props.rootId ?? currentChannelId,
            isThread: props.isThread,
        });

        return true;
    };

    const clearPost = () => {
        return new Promise<boolean>((resolve) => {
            editorRef.current?.commands.clearContent(false);
            setSubmitting(false);
            setFileInfos([]);
            setUploadsInProgress([]);
            setQuote(undefined);
            setMessage('');

            clearChannelDraft();
            handleSaveDraftOnMessageChange('', []);
            resolve(true);
        });
    };

    const getCreatePostSubmenuProps = () => {
        return {
            getPostText: getMarkdown,
            getPostFiles: () => fileInfos,
            clearPost,
            rootId: props.rootId,
        };
    };

    const handleEmojiClose = () => {
        setShowEmojiPicker(false);
        focusTextbox();
    };

    const handleEmojiClick = (emoji: Emoji) => {
        const emojiAlias = ('short_names' in emoji && emoji.short_names && emoji.short_names[0]) || emoji.name;

        if (!emojiAlias) {
            return;
        }

        const nodesToInsert = [
            {
                type: 'TimeWebkitEmojiExtension',
                attrs: {label: emojiAlias},
            },
            {
                type: 'text',
                text: ' ',
            },
        ];

        if (caretPosition !== 1) {
            nodesToInsert.unshift({
                type: 'text',
                text: ' ',
            });
        }

        editorRef.current?.chain().insertContentAt(caretPosition, nodesToInsert).run();

        handleEmojiClose();
    };

    const toggleEmojiPicker = () => {
        if (!showEmojiPicker) {
            sendToStatist('searchEmoji.tap', {
                sourceTap: props.isThread ? 'thread' : 'post',
            });
        }

        setShowEmojiPicker(!showEmojiPicker);
        if (showEmojiPicker) {
            focusTextbox();
        }
    };

    const isEditSaveDisabled = () => {
        if (message.length > props.maxPostSize || hashtagError) {
            return true;
        }

        if (fileInfos.length > 0) {
            return !props.canEditPost;
        }

        if (!isContentBlockEmpty(editorRef.current?.getJSON())) {
            return !props.canEditPost;
        }

        return !props.canDeletePost;
    };

    const setMessageAndCaretPosition = (message: string, caretPosition: number) => {
        setMessage(message);
        setCaretPosition(caretPosition);

        setMarkdownToEditor(message, false);
    };

    const resetPostState = (shouldBlur = true) => {
        setMessageAndCaretPosition('', 0);
        if (shouldBlur) {
            editorRef.current?.chain().clearContent(false).blur().run();
        }

        setFileInfos([]);
        setUploadsInProgress([]);
        setQuote(undefined);
        setMessage('');
        handleSaveDraftOnMessageChange('', []);
        setSubmitting(false);
    };

    const shouldEnableSendButton = () => {
        if (message.length > props.maxPostSize || hashtagError) {
            return false;
        }

        const isMessageEmpty = isContentBlockEmpty(editorRef.current?.getJSON());
        const hasFileInfos = Boolean(fileInfos?.length);

        return !isMessageEmpty || hasFileInfos;
    };

    const focusTextbox = useCallback(
        (keepFocus = false) => {
            if (!editorRef.current || editorRef.current?.isDestroyed) {
                return;
            }

            const postTextboxDisabled = !props.canPost;
            if (postTextboxDisabled) {
                editorRef.current?.chain().blur().run();
                return;
            }

            if (keepFocus || !UserAgent.isMobile()) {
                editorRef.current?.chain().focus().run();
            }
        },
        [props.canPost],
    );

    // делал ребэйз нужно будет разобраться
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleFileUploadChange = useCallback(() => focusTextbox(), []);

    const handleUploadStart = useCallback(
        (clientIds: string[]) => {
            setUploadsInProgress((prev) => [...prev, ...clientIds]);
            focusTextbox();
        },
        [focusTextbox],
    );

    const handleUploadProgress = useCallback((filePreviewInfo: FilePreviewInfo) => {
        setUploadsProgressPercent((prev) => ({
            ...prev,
            [filePreviewInfo.clientId]: filePreviewInfo,
        }));
    }, []);

    const handleFileUploadComplete = useCallback(
        (completedFileInfos: FileInfo[], clientIds: string[]) => {
            setUploadsInProgress((prev) => prev.filter((uploadInProgress) => !clientIds.includes(uploadInProgress)));
            setFileInfos((prev) => {
                const newFileInfos = sortFileInfos(prev.concat(completedFileInfos), props.locale);
                saveDraft(message, newFileInfos, quote);
                return newFileInfos;
            });
        },
        [props.locale, message, quote, saveDraft],
    );

    const handleUploadServerError = useCallback((err: string | ServerError | null, clientId?: string) => {
        let serverError = err;

        if (typeof serverError === 'string') {
            serverError = new Error(serverError);
        }

        setServerError(serverError);
        setUploadsInProgress((prev) => prev.filter((item) => item !== clientId));
    }, []);

    const handleUploadClientError = useCallback((error: Error | null) => {
        setClientUploadError(error ? error.message : null);
    }, []);

    const unsetEdit = useEventCallback((refocusId?: string, onlyLocal = false) => {
        if (refocusId) {
            const element = document.getElementById(refocusId);
            element?.focus();
        }
        if (!onlyLocal) {
            props.actions.unsetEditingPost();
        }

        editorRef.current?.commands.setMarkdown(props.draft.message, true);

        setFileInfos(props.draft.fileInfos);
        setQuote(props.draft.quote);
        setMessage(props.draft.message);
        setServerError(null);
        setClientUploadError(null);
    });

    const handlePostQuoteClose = () => {
        setQuote(undefined);
        saveDraft(message, fileInfos, null);
        if (props.isEditing) {
            unsetEdit();
        }
    };

    const handleEscPress = useEventCallback(() => {
        handlePostQuoteClose();
    });

    const handleFocusReturn = useEventCallback(() => {
        editorRef.current?.chain().focus().run();
    });

    const handleChange = useEventCallback((newMessage: string) => {
        let error = serverError;

        if (isErrorInvalidSlashCommand(error) || isNotAvailableServerError(error)) {
            error = null;
        }

        if (!props.isEditing) {
            emitTypingEvent(newMessage);
        }
        setServerError(error);
        setMessage(newMessage);
        handleSaveDraftOnMessageChange(newMessage);
    });

    const emitLocalUserTypingEventDebounced = useMemo(
        () => debounce((channelId: string, parentId: string, message: string) => {
            if (!message) {
                return;
            }
            GlobalActions.emitLocalUserTypingEvent(channelId, parentId);
        }, 1000, {leading: true}), []);

    const emitTypingEvent = useCallback((message: string) => {
        emitLocalUserTypingEventDebounced(props.currentChannelId, props.rootId ?? '', message);
    }, [props.currentChannelId, props.rootId, emitLocalUserTypingEventDebounced]);

    const postMsgKeyPress = useEventCallback((e: KeyboardEvent<Element>) => {
        const {ctrlSend, codeBlockOnCtrlEnter} = props;
        const {
            allowSending,
            withClosedCodeBlock,
            ignoreKeyPress,
            message: postMessage,
        } = postMessageOnKeyPressAdvanced(
            e,
            message,
            Boolean(ctrlSend),
            Boolean(codeBlockOnCtrlEnter),
            Date.now(),
            lastChannelSwitchAt,
            caretPosition,
        ) as {
            allowSending: boolean;
            withClosedCodeBlock?: boolean;
            ignoreKeyPress?: boolean;
            message?: string;
        };

        if (ignoreKeyPress) {
            e.preventDefault();
            e.stopPropagation();
            return false;
        }

        if (allowSending) {
            if (withClosedCodeBlock && postMessage) {
                editorRef.current?.commands.setMarkdown(postMessage, false);
            }

            if (props.isEditing) {
                handleEdit();
                return true;
            }

            handleSubmit(e);
            emitTypingEvent('');
            return true;
        }

        return false;
    });

    const showNotifyAllModal = (mentions: string[], channelTimezoneCount: number, memberNotifyCount: number) => {
        props.actions.openModal({
            modalId: ModalIdentifiers.NOTIFY_CONFIRM_MODAL,
            dialogType: NotifyConfirmModal,
            dialogProps: {
                mentions,
                channelTimezoneCount,
                memberNotifyCount,
                onConfirm: () => doSubmit(),
            },
        });
    };

    const sendMessage = async (originalPost: Post) => {
        const {
            currentChannelId,
            currentUserId,
            useLDAPGroupMentions,
            useChannelMentions,
            groupsWithAllowReference,
            useCustomGroupMentions,
        } = props;

        const {runMessageWillBePostedHooks, onSubmitPost, scrollPostListToBottom} = props.actions;

        let post = originalPost;

        post.channel_id = currentChannelId;

        const time = getTimestamp();
        const userId = currentUserId;

        post.pending_post_id = `${userId}:${time}`;
        post.user_id = userId;
        post.create_at = time;
        post.metadata = {} as PostMetadata;
        post.props = {};

        if (!useChannelMentions && containsAtChannel(post.message, {checkAllMentions: true})) {
            post.props.mentionHighlightDisabled = true;
        }

        if (
            !useLDAPGroupMentions &&
            !useCustomGroupMentions &&
            groupsMentionedInText(post.message, groupsWithAllowReference)
        ) {
            post.props.disable_group_highlight = true;
        }

        if (quote) {
            post.props.quote_post_id = quote.id;
        }

        const hookResult = await runMessageWillBePostedHooks(post);

        if (hookResult.error) {
            setServerError(hookResult.error);
            setClientUploadError(null);
            setSubmitting(false);

            return hookResult;
        }

        post = hookResult.data;

        await onSubmitPost(post, fileInfos);

        if (!props.isThread) {
            scrollPostListToBottom();
        }

        setSubmitting(false);

        return {data: true};
    };

    const sendReaction = async (isReaction: RegExpExecArray) => {
        const {latestReplyablePostId} = props;
        const {addReaction, removeReaction} = props.actions;

        const action = isReaction[1];
        const emojiName = isReaction[2];
        const postId = latestReplyablePostId;

        if (postId && action === '+') {
            await addReaction(postId, emojiName!);
        } else if (postId && action === '-') {
            await removeReaction(postId, emojiName!);
        }

        await clearChannelDraft();
    };

    const handleSubmittedPost = (success = true) => {
        setSubmitting(false);

        if (!success) {
            return;
        }

        setMessage('');
        setQuote(undefined);

        focusTextbox();
        clearChannelDraft();
    };

    const doSubmit = async (e?: FormEvent) => {
        if (e) {
            e.preventDefault?.();
        }

        const message = getMarkdown();

        const {currentTeamId, currentChannelId: channelId, actions} = props;
        const {runSlashCommandWillBePostedHooks} = actions;

        let postMessage = message;
        let ignoreSlash = false;

        if (serverError && isErrorInvalidSlashCommand(serverError) && serverError.submittedMessage === postMessage) {
            postMessage = serverError.submittedMessage;
            ignoreSlash = true;
        }

        const post = {} as Post;
        post.file_ids = [];
        post.message = postMessage;

        if (props.rootId) {
            post.root_id = props.rootId;
        }

        mark(Marks.MESSAGE_SENT, {
            detail: {
                isThread: Boolean(props.isThread),
                channelId,
            },
        });
        props.actions.addMessageIntoHistory(message);

        setSubmitting(true);
        setServerError(null);
        setClientUploadError(null);

        focusTextbox();

        const isReaction = Utils.REACTION_PATTERN.exec(post.message);

        const isSlashCommandMessage = post.message.indexOf('/');

        // Состояние поста (текст, цитата, драфт) не должно очищаться, если сообщение не было отправлено.
        // Пример: пользователь попытался отправить сообщение, с несуществующей командой.
        // В этом случае мы сразу не отправляем пост, а сначала показываем предупрждение.
        let submitFailed = false;

        mark(Marks.MESSAGE_LOADING_STARTED);
        if (isSlashCommandMessage === 0 && !ignoreSlash) {
            let args: CommandArgs = {
                channel_id: channelId,
                team_id: currentTeamId,
                root_id: props.rootId,
            };

            const hookResult = await runSlashCommandWillBePostedHooks(post.message, args);

            if (hookResult.error) {
                setServerError({...hookResult.error, submittedMessage: post.message});
                setClientUploadError(null);
                setMessage(post.message);

                setMarkdownToEditor(post.message, false);
                submitFailed = true;
            } else if (hookResult.data.message && hookResult.data.args) {
                post.message = hookResult.data.message;
                args = hookResult.data.args;
                const {executeCommand} = props.actions;

                const {error} = await executeCommand(post.message, args);

                if (error) {
                    if (error.sendMessage) {
                        await sendMessage(post);

                        resetPostState();
                    } else {
                        setServerError({...error, submittedMessage: post.message});
                        setClientUploadError(null);

                        setMarkdownToEditor(post.message, false);
                        submitFailed = true;
                    }
                } else {
                    resetPostState();
                }
            }
        } else if (isReaction && props.emojiMap.has(isReaction[2]!)) {
            await sendReaction(isReaction);

            resetPostState(false);
        } else {
            const {error} = await sendMessage(post);

            if (!error) {
                resetPostState(false);
            }
        }
        mark(Marks.MESSAGE_LOADING_FINISHED);
        handleSubmittedPost(!submitFailed);
    };

    const canSubmit = () => {
        // Пользователь ничего не заполнил и не приложил файлы
        const isEmpty = isContentBlockEmpty(editorRef.current?.getJSON()) && fileInfos.length === 0;

        if (submitting || isEmpty || message?.length > props.maxPostSize ||
            hashtagError || uploadsInProgress.length > 0) {
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: FormEvent) => {
        e?.preventDefault?.();
        const {
            userIsOutOfOffice,
            groupsWithAllowReference,
            currentChannelMembersCount,
            useLDAPGroupMentions,
            useCustomGroupMentions,
            enableConfirmNotificationsToChannel,
            useChannelMentions,
            currentChannelId,
            isTimezoneEnabled,
        } = props;

        if (!canSubmit()) {
            handleFocusReturn();
            return;
        }

        const {getChannelTimezones, openModal, openEditChannelHeaderModal, openEditChannelPurposeModal} = props.actions;

        const markdownMessage = getMarkdown();

        setSubmitting(true);

        const notificationsToChannel = enableConfirmNotificationsToChannel && useChannelMentions;
        let memberNotifyCount = 0;
        let channelTimezoneCount = 0;
        let mentions: string[] = [];

        const specialMentions = specialMentionsInText(markdownMessage);
        const hasSpecialMentions = Object.values(specialMentions).includes(true);

        if (
            !hasSpecialMentions &&
            (useLDAPGroupMentions || useCustomGroupMentions)
        ) {
            // Groups mentioned in users text
            const mentionGroups = groupsMentionedInText(markdownMessage, groupsWithAllowReference);

            if (mentionGroups.length > 0) {
                const [channelGroupsMembersCount] = await asyncResult(
                    Client4.getChannelMemberCountsByGroupIDs(
                        currentChannelId,
                        mentionGroups.map((group) => group.id),
                        true),
                );

                const channelGroupsMembersCountDict = keyBy(channelGroupsMembersCount, (el) => el.group_id);

                mentionGroups.forEach((group) => {
                    if (group.source === 'ldap' && !useLDAPGroupMentions) {
                        return;
                    }
                    if (group.source === 'custom' && !useCustomGroupMentions) {
                        return;
                    }
                    const mappedValue = channelGroupsMembersCountDict[group.id];
                    if (mappedValue) {
                        memberNotifyCount += mappedValue.channel_member_count;
                        channelTimezoneCount += mappedValue.channel_member_timezones_count;
                    }
                    mentions.push(`@${group.name}`);
                });
                mentions = [...new Set(mentions)];
            }
        }

        if (hasSpecialMentions) {
            memberNotifyCount = currentChannelMembersCount;

            for (const k in specialMentions) {
                if (specialMentions[k] === true) {
                    mentions.push('@' + k);
                }
            }

            if (isTimezoneEnabled) {
                const {data} = await getChannelTimezones(currentChannelId);
                channelTimezoneCount = data ? data.length : 0;
            }
        }

        if (notificationsToChannel && memberNotifyCount > Constants.NOTIFY_ALL_MEMBERS) {
            setSubmitting(false);
            showNotifyAllModal(mentions, channelTimezoneCount, memberNotifyCount);
            return;
        }

        const status = getStatusFromSlashCommand(markdownMessage);

        if (userIsOutOfOffice && isStatusSlashCommand(status)) {
            const resetStatusModalData = {
                modalId: ModalIdentifiers.RESET_STATUS,
                dialogType: ResetStatusModal,
                dialogProps: {newStatus: status},
            };

            openModal(resetStatusModalData);

            resetPostState();
            return;
        }

        if (trimRight(markdownMessage) === '/header') {
            openEditChannelHeaderModal();

            resetPostState();
            return;
        }

        if (![ChannelType.DM, ChannelType.GM].includes(props.currentChannelType) &&
            trimRight(markdownMessage) === '/purpose'
        ) {
            openEditChannelPurposeModal();

            resetPostState();
            return;
        }

        await doSubmit(e);
    };

    const editLastPostKeyPress = useEventCallback((e: KeyboardEvent<Element>) => {
        const lastPost = props.currentUsersLatestPost;
        const {setEditingPost} = props.actions;

        const shouldOpenEdit = editLastPostKeyPressAdvanced(e);

        if (!lastPost || !shouldOpenEdit || props.isEditing || message) {
            return false;
        }

        let type;
        if (lastPost.root_id && lastPost.root_id.length > 0) {
            type = Utils.localizeMessage('create_post.comment', Posts.MESSAGE_TYPES.COMMENT);
        } else {
            type = Utils.localizeMessage('create_post.post', Posts.MESSAGE_TYPES.POST);
        }
        setEditingPost(lastPost.id, '', type, props.isThread);

        return true;
    });

    const handleReactLastPost = useEventCallback(() => {
        if (message) {
            return false;
        }

        const location = props.isThread ? Locations.RHS_ROOT : Locations.CENTER;
        props.actions.emitShortcutReactToLastPostFrom(location);

        return true;
    });

    const placeholder = useGetCreateMessage({
        isThread: props.isThread,
        currentChannelType: props.currentChannelType,
        currentChannelDisplayName: props.currentChannelDisplayName,
        currentUserName: props.currentUserName,
        formatMessage: props.intl.formatMessage,
    });

    const handleEdit = async () => {
        if (!props.editingPost.post || isEditSaveDisabled()) {
            return;
        }

        const fileIds = fileInfos.map((fileInfo: FileInfo) => fileInfo.id);

        const updatedPost = {
            message,
            id: props.editingPost.postId,
            channel_id: props.editingPost.post?.channel_id,
            file_ids: fileIds,
        };

        const messageHasNotChanged =
            updatedPost.message === (props.editingPost.post?.message_source || props.editingPost.post?.message);

        const filesHasNotChanged =
            fileIds.length === props.editingPost.post?.file_ids?.length &&
            fileIds.every((id: string) => props.editingPost.post?.file_ids?.includes(id));

        if (messageHasNotChanged && filesHasNotChanged) {
            unsetEdit(props.editingPost.refocusId);
            return;
        }

        const hasAttachment = fileIds.length;

        if (isContentBlockEmpty(editorRef.current?.getJSON()) && !hasAttachment) {
            unsetEdit();

            const deletePostModalData = {
                modalId: ModalIdentifiers.DELETE_POST,
                dialogType: DeletePostModal,
                dialogProps: {
                    post: props.editingPost.post,
                    isRHS: props.editingPost.isRHS,
                },
            };

            props.actions.openModal(deletePostModalData);
            return;
        }

        setEditing(true);

        props.actions
            .editPost(updatedPost as Post)
            .then((res: any) => {
                if (res.error) {
                    setServerError({
                        ...res.error,
                        message: Utils.localizeMessage('post.edit.error', 'Failed to save, please try again'),
                    });
                    setClientUploadError(null);
                    return;
                }

                unsetEdit(props.editingPost.refocusId);
            })
            .finally(() => {
                setEditing(false);
            });
    };

    const handleReplyLastPost = useEventCallback(() => {
        const lastPost = props.currentChannelLatestPost;

        if (message || !props.canPost || !lastPost || isSystemMessage(lastPost)) {
            return false;
        }

        window.postMessage(
            {
                type: 'reply_to_message',
                isRHS: props.isThread,
                location: props.isThread ? Locations.RHS_COMMENT : Locations.CENTER,
                post: props.currentChannelLatestPost,
            },
            window.location.origin,
        );

        return true;
    });

    const removePreview = (removedId: string) => {
        if (uploadsInProgress.includes(removedId)) {
            fileUploadRef.current?.cancelUpload(removedId);
        }
        setFileInfos((prev) => {
            saveDraft(
                message,
                prev.filter(({id}) => id !== removedId),
                quote,
            );
            return prev.filter(({id}) => id !== removedId);
        });
        setUploadsInProgress((prev) => prev.filter((id) => id !== removedId));

        setServerError(null);
        setClientUploadError(null);

        handleFileUploadChange();
    };

    const setEditor = useCallback((editor: IEditor | null) => {
        editor?.commands.focus(undefined, {scrollIntoView: false});
        editorRef.current = editor;
    }, []);

    const handleCaretPositionChange = useMemo(() => debounce(setCaretPosition, 100), []);
    const handleKeyDown = useEventCallback((e: globalThis.KeyboardEvent) => {
        if (submitting) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        const isPageDownPressed = Utils.isKeyPressed(e, KeyCodes.PAGE_DOWN);
        const isPageUpPressed = Utils.isKeyPressed(e, KeyCodes.PAGE_UP);

        if (isPageDownPressed || isPageUpPressed) {
            const textbox = e.target as HTMLDivElement;
            return props.scrollListPage?.(textbox, isPageDownPressed ? 'down' : 'up');
        }
    });

    const onChangeEditingPost = useEventCallback((isEditing) => {
        if (isEditing) {
            setMarkdownToEditor(props.editingPost?.post?.message ?? '', true);

            setFileInfos(props.editingPost?.fileInfos);
            setMessage(props.editingPost?.post?.message ?? '');
            setQuote(props.editingPost.post);

            focusTextbox();
            emitTypingEvent('');
        }
        if (!isEditing) {
            unsetEdit(undefined, true);
        }
    });
    useEffect(() => {
        onChangeEditingPost(props.isEditing);
    }, [
        props.isEditing,
        props.editingPost.postId,
        onChangeEditingPost,
    ]);

    const pluginItemProps = getCreatePostSubmenuProps();
    const pluginItems = getPluginItems(props.pluginMenuItems || [], pluginItemProps);
    const predefinedMentions = props.useChannelMentions ? SPECIAL_MENTIONS : undefined;

    const attachmentsDisabledClass = props.canUploadFiles ? '' : 'post-create--attachment-disabled';

    return {
        props,
        state: {
            quote,
            caretPosition,
            submitting,
            editing,
            uploadsInProgress,
            uploadsProgressPercent,
            showEmojiPicker,
            serverError,
            postError,
            fileInfos,
            message,
        },
        handlers: {
            setEditor,
            handleCaretPositionChange,
            handleKeyDown,
            handleReplyLastPost,
            handleReactLastPost,
            handleUploadStart,
            handleUploadProgress,
            handleFileUploadChange,
            handleFileUploadComplete,
            handleUploadServerError,
            handleUploadClientError,
            handlePostQuoteClose,
            handleChange,
            handleEmojiClick,
            postMsgKeyPress,
            handleSubmit,
            handleEmojiClose,
            editLastPostKeyPress,
            handleEdit,
            handleEscPress,
            handleFocusReturn,
        },
        actions: {
            removePreview,
            shouldEnableSendButton,
            toggleEmojiPicker,
            isEditSaveDisabled,
            setServerError,
        },
        refs: {
            topDiv,
            fileUploadRef,
            createPostControlsRef,
            emojiPickerRef,
        },
        classNames: {
            attachmentsDisabledClass,
        },
        pluginItems,
        predefinedMentions,
        editorReference: editorRef.current,
        placeholder,
    };
};
